import React, { useEffect, useMemo, useState } from 'react';

export default function Payroll() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { date, userId, amount, note }
  const [year, setYear] = useState(new Date().getFullYear());

  const load = async (y) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/work/payrollhistory?year=${y}`);
      const data = await res.json();
      const filtered = (data || []).filter(r => Number(r.earningsYear || 0) > 0);
      setRows(filtered);
      const ures = await fetch('/api/work/getusers');
      const udata = await ures.json();
      setUsers(udata);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(year); }, [year]);

  const toCurrency = (n) => Number(n||0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const totals = useMemo(() => {
    // Year-only totals across rows (already filtered)
    const earnings = rows.reduce((s, r) => s + Number(r.earningsYear || 0), 0);
    const paid = rows.reduce((s, r) => s + Number(r.paidYear || 0), 0);
    return { earnings, paid, balance: earnings - paid };
  }, [rows]);

  const startAdd = (userId) => {
    const d = new Date();
    setEditing({ date: d.toISOString().substring(0,10), userId: userId||'', amount: 0, note: '' });
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing?.userId) return;
    const body = { ...editing, date: new Date(editing.date).toISOString(), amount: Number(editing.amount||0) };
    const res = await fetch('/api/work/payrollpayment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!res.ok) { console.error('save failed'); return; }
    setEditing(null);
    await load(year);
  };

  const remove = async (id) => {
    const res = await fetch(`/api/work/payrollpayment/${id}`, { method: 'DELETE' });
    if (!res.ok) { console.error('delete failed'); return; }
    await load(year);
  };

  const shiftYear = (delta) => setYear(y => y + delta);

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h2>Payroll - {year}</h2>

      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => shiftYear(-1)}>&lt;</button>
        <span><h3></h3></span>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => shiftYear(1)}>&gt;</button>
      </div>

      {/* Top totals with label on left and amount aligned right */}
      <div className="mb-3">
        <table className="table table-sm w-auto mb-0">
          <tbody>
            <tr>
              <td className="text-nowrap pe-3">Earned</td>
              <td className="text-end fw-bold">{toCurrency(totals.earnings)}</td>
            </tr>
            <tr>
              <td className="text-nowrap pe-3">Paid</td>
              <td className="text-end fw-bold">{toCurrency(totals.paid)}</td>
            </tr>
            <tr>
              <td className="text-nowrap pe-3">Outstanding</td>
              <td className="text-end fw-bold">{toCurrency(totals.balance)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {!editing && <button className="btn btn-primary mb-3" onClick={() => startAdd('')}>Add payment</button>}

      {editing && (
        <div className="card p-3 mb-3">
          <div className="row g-2">
            <div className="col-md-2">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={editing.date} onChange={e => setEditing(prev => ({ ...prev, date: e.target.value }))} />
            </div>
            <div className="col-md-3">
              <label className="form-label">User</label>
              <select className="form-select" value={editing.userId} onChange={e => setEditing(prev => ({ ...prev, userId: e.target.value }))}>
                <option value="">Select user...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.id}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Amount</label>
              <input type="number" step="0.01" className="form-control" value={editing.amount} onChange={e => setEditing(prev => ({ ...prev, amount: e.target.value }))} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Note</label>
              <input type="text" className="form-control" value={editing.note} onChange={e => setEditing(prev => ({ ...prev, note: e.target.value }))} />
            </div>
            <div className="col-md-12 mt-2">
              <button className="btn btn-success me-2" onClick={save}>Save</button>
              <button className="btn btn-secondary" onClick={cancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {rows.map(r => {
        const paidYear = Number(r.paidYear || 0);
        const balanceYear = Number(r.earningsYear || 0) - paidYear;
        return (
          <div key={r.user.id} className="card p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{r.user.name || r.user.id}</h5>
              <button className="btn btn-sm btn-outline-primary" onClick={() => startAdd(r.user.id)}>Add payment</button>
            </div>

            {/* Per-user summary with label on left and amount aligned right */}
            <div className="mt-2">
              <table className="table table-sm w-auto mb-0">
                <tbody>
                  <tr>
                    <td className="text-nowrap pe-3">Earned</td>
                    <td className="text-end fw-bold">{toCurrency(r.earningsYear || 0)}</td>
                  </tr>
                  <tr>
                    <td className="text-nowrap pe-3">Outstanding</td>
                    <td className="text-end fw-bold">{toCurrency(balanceYear)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <table className="table table-sm mt-2">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(r.payments||[]).map(p => (
                  <tr key={p.id}>
                    <td>{new Date(p.date).toLocaleDateString()}</td>
                    <td>{toCurrency(p.amount)}</td>
                    <td>{p.note}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(p.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {(!r.payments || r.payments.length === 0) && (
                  <tr><td colSpan="4" className="text-muted">No payments in {year}.</td></tr>
                )}
                <tr>
                  <td></td>
                  <td><strong>{toCurrency(paidYear)}</strong></td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
